import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Handshake } from "lucide-react";
import { toast } from "sonner";
import { AdminShell, AdminCrud, ReadTable, SettingsForm, InvoiceDashboard } from "@/components/adminkit";
import { MODULES } from "@/lib/adminConfig";
import { VEHICLE_ICON_LIST } from "@/lib/fleet";

const ROLE_OPTS = [{ v: "admin", l: "Administrateur" }, { v: "gestionnaire", l: "Gestionnaire" }, { v: "user", l: "Utilisateur" }];
const UNIT_TYPES = [{ v: "camion", l: "Camion" }, { v: "utilitaire", l: "Utilitaire" }, { v: "vul", l: "VUL" }, { v: "voiture", l: "Voiture" }, { v: "bus", l: "Bus" }, { v: "bateau", l: "Bateau" }, { v: "jetski", l: "Jet-ski" }, { v: "voilier", l: "Voilier" }];
const ICON_OPTS = VEHICLE_ICON_LIST.map((i) => ({ v: i.key, l: i.label }));

const CompaniesAdmin = () => (
  <AdminCrud endpoint="/companies" title="Compagnies" description="Créez, modifiez et supprimez les compagnies clientes."
    fields={[{ key: "name", label: "Nom", type: "text" }, { key: "contact_name", label: "Contact", type: "text" }, { key: "phone", label: "Téléphone", type: "text" }, { key: "email", label: "Email", type: "text" }]} />
);

const UsersAdmin = () => (
  <AdminCrud endpoint="/users" title="Utilisateurs" description="Gérez les comptes et leurs niveaux d'accès." idKey="user_id"
    columns={[{ key: "name", label: "Nom" }, { key: "email", label: "Email" }, { key: "role", label: "Rôle" }]}
    createFields={[{ key: "name", label: "Nom", type: "text" }, { key: "email", label: "Email", type: "text" }, { key: "role", label: "Rôle", type: "select", options: ROLE_OPTS }, { key: "password", label: "Mot de passe (min 6)", type: "password" }]}
    editFields={[{ key: "name", label: "Nom", type: "text" }, { key: "role", label: "Rôle", type: "select", options: ROLE_OPTS }]} />
);

function UnitsAdmin() {
  const [refs, setRefs] = useState({ devices: [] });
  useEffect(() => { api.get("/devices").then((r) => setRefs({ devices: r.data.map((d) => ({ v: d.id, l: `${d.name} (${d.imei})` })) })); }, []);
  const fields = [
    { key: "name", label: "Nom", type: "text" }, { key: "plate", label: "Immatriculation", type: "text" },
    { key: "type", label: "Type", type: "select", options: UNIT_TYPES }, { key: "device_id", label: "Boîtier", type: "select", optionsRef: "devices" },
    { key: "driver", label: "Chauffeur", type: "text" }, { key: "max_speed_kmh", label: "Vitesse max (km/h)", type: "number" },
    { key: "color", label: "Couleur", type: "color" },
    { key: "icon", label: "Icône", type: "iconpicker" },
  ];
  return <AdminCrud endpoint="/vehicles" title="Unités" description="Créez, modifiez et supprimez les unités (véhicules)." fields={fields} refs={refs}
    columns={[{ key: "name", label: "Nom" }, { key: "icon", label: "Icône" }, { key: "plate", label: "Immat." }, { key: "type", label: "Type" }, { key: "driver", label: "Chauffeur" }]} />;
}


const INVITE_STATUS = { en_attente: { l: "En attente", c: "#F5821F" }, utilise: { l: "Utilisé", c: "#10B981" }, expire: { l: "Expiré", c: "#EF4444" } };

function DemoInvitePanel() {
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/demo/invites").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/demo/invite", { email: email.trim() });
      toast.success(`Lien de démo envoyé à ${email.trim()}`);
      setEmail(""); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setLoading(false); }
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div data-testid="demo-invite-panel">
      <h2 className="text-lg font-bold mb-1">Envoyer une démo</h2>
      <p className="text-sm text-muted-foreground mb-4">Saisissez l'email d'un prospect : il recevra un lien d'accès à la démonstration, valable 72 h et utilisable une seule fois.</p>
      <form onSubmit={send} className="flex flex-col sm:flex-row gap-2 max-w-xl mb-6">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email.du.prospect@exemple.com"
          data-testid="demo-invite-email"
          className="flex-1 bg-secondary/60 border border-border rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#17A2B8] text-foreground placeholder:text-muted-foreground" />
        <button type="submit" disabled={loading} data-testid="demo-invite-send"
          className="flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-5 py-2.5 text-sm transition-colors">
          {loading ? "Envoi…" : "Envoyer le lien"}
        </button>
      </form>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr className="label-mono text-left"><th className="px-4 py-3">Email prospect</th><th className="px-4 py-3">Envoyé le</th><th className="px-4 py-3">Expire le</th><th className="px-4 py-3">Statut</th></tr></thead>
          <tbody className="data">
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Aucune démo envoyée</td></tr>}
            {rows.map((r) => {
              const st = INVITE_STATUS[r.status] || INVITE_STATUS.en_attente;
              return (
                <tr key={r.id} className="border-t border-border" data-testid={`demo-invite-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold">{r.email}</td>
                  <td className="px-4 py-3">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3">{fmt(r.expires_at)}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: st.c }}><span className="w-2 h-2 rounded-full" style={{ background: st.c }} />{st.l}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const GROUPS = [
  { group: "Clients", items: [
    { k: "compagnies", label: "Compagnies" }, { k: "units", label: "Unités" }, { k: "users", label: "Utilisateurs" },
    { k: "demo_invite", label: "Envoyer une démo" },
    { k: "resellers", label: "Revendeurs" }, { k: "summary", label: "Résumé" },
  ] },
  { group: "Information", items: [{ k: "service_types", label: "Comparaison de Type de Service" }] },
  { group: "Admin", items: [
    { k: "langue", label: "Langue" }, { k: "leads", label: "Contacts potentiels" }, { k: "access", label: "Pousser le Niveau d'accès" },
    { k: "custom_names", label: "Noms Personnalisés" }, { k: "sensor_calibration", label: "Calibrage du Capteur" },
    { k: "emplacement", label: "Gestion Emplacement" }, { k: "queued_messages", label: "Type des Messages en file d'attente" },
    { k: "command_names", label: "Nom de la Commande" },
  ] },
  { group: "Structure de tarification", items: [{ k: "pricing", label: "Tarification" }, { k: "facturation", label: "Facturation" }] },
  { group: "Appareils", items: [{ k: "balises", label: "Balises" }, { k: "sim_cards", label: "Sim" }, { k: "inventory", label: "Inventaire" }, { k: "modeles", label: "Modèles" }] },
  { group: "Dépôt", items: [{ k: "dep_sales", label: "Ventes et Marketing" }, { k: "dep_tech", label: "Technique" }] },
];

function Summary() {
  const [d, setD] = useState({ companies: 0, units: 0, users: 0, drivers: 0, total: 0, count: 0 });
  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/vehicles"), api.get("/users"), api.get("/drivers"), api.get("/invoices/stats")])
      .then(([c, v, u, dr, s]) => setD({ companies: c.data.length, units: v.data.length, users: u.data.length, drivers: dr.data.length, total: s.data.total, count: s.data.count }));
  }, []);
  const cards = [
    { l: "Compagnies", v: d.companies }, { l: "Unités", v: d.units }, { l: "Utilisateurs", v: d.users },
    { l: "Conducteurs", v: d.drivers }, { l: "Factures", v: d.count }, { l: "Total facturé", v: `${Math.round(d.total).toLocaleString("fr-FR")} XPF` },
  ];
  return (
    <div>
      <h2 className="text-lg font-bold mb-3">Résumé partenaire</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => <div key={c.l} className="glass rounded-lg p-4" data-testid={`summary-${c.l}`}><p className="label-mono">{c.l}</p><p className="text-2xl font-extrabold data mt-1 brand-teal">{c.v}</p></div>)}
      </div>
    </div>
  );
}

function ModelsTable() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/device-models").then((r) => setRows(Object.entries(r.data).map(([k, v]) => ({ code: k, ...v })))); }, []);
  return (
    <div>
      <h2 className="text-lg font-bold mb-3">Modèles d'appareils</h2>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-secondary/50"><tr className="label-mono text-left"><th className="px-4 py-3">Code</th><th className="px-4 py-3">Libellé</th><th className="px-4 py-3">Protocole</th><th className="px-4 py-3">Transport</th></tr></thead>
        <tbody className="data">{rows.map((r) => <tr key={r.code} className="border-t border-border"><td className="px-4 py-3 font-semibold">{r.code}</td><td className="px-4 py-3">{r.label}</td><td className="px-4 py-3">{r.protocol}</td><td className="px-4 py-3">{r.transport}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
}

export default function AdminPartnerPage() {
  const [tab, setTab] = useState("compagnies");
  const render = () => {
    if (MODULES[tab]) return <AdminCrud {...MODULES[tab]} />;
    switch (tab) {
      case "compagnies": return <CompaniesAdmin />;
      case "demo_invite": return <DemoInvitePanel />;
      case "units": return <UnitsAdmin />;
      case "users": return <UsersAdmin />;
      case "access": return <ReadTable endpoint="/users" title="Niveaux d'accès" description="Les rôles se modifient dans Administration > Gestion utilisateur." columns={[{ key: "name", label: "Nom" }, { key: "email", label: "Email" }, { key: "role", label: "Niveau d'accès" }]} />;
      case "emplacement": return <ReadTable endpoint="/geofences" title="Gestion Emplacement (zones)" columns={[{ key: "name", label: "Nom" }, { key: "type", label: "Type" }, { key: "kind", label: "Nature" }]} />;
      case "balises": return <ReadTable endpoint="/devices" title="Balises (boîtiers)" columns={[{ key: "name", label: "Nom" }, { key: "imei", label: "IMEI" }, { key: "sim_number", label: "SIM" }, { key: "model", label: "Modèle" }, { key: "protocol", label: "Protocole" }]} />;
      case "modeles": return <ModelsTable />;
      case "summary": return <Summary />;
      case "facturation": return <InvoiceDashboard />;
      case "langue": return <SettingsForm group="langue" title="Langue" description="Préférences de langue et de format." fields={[{ key: "lang", label: "Langue", type: "select", options: [{ v: "fr", l: "Français" }, { v: "en", l: "English" }] }, { key: "timezone", label: "Fuseau horaire", type: "text" }, { key: "date_format", label: "Format de date", type: "text" }]} />;
      case "custom_names": return <SettingsForm group="custom_names" title="Noms Personnalisés" description="Renommez les libellés de l'interface." fields={[{ key: "label_unit", label: "Libellé « Unité »", type: "text" }, { key: "label_driver", label: "Libellé « Conducteur »", type: "text" }, { key: "label_zone", label: "Libellé « Zone »", type: "text" }]} />;
      default: return null;
    }
  };
  return (
    <Layout>
      <AdminShell title="Administration Partenaire" icon={Handshake} groups={GROUPS} active={tab} setActive={setTab}>
        {render()}
      </AdminShell>
    </Layout>
  );
}
